import { useState } from 'react';
import { Upload, FileCheck2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import CrmContactPicker from './CrmContactPicker.jsx';
import {
  createAgencyLicense, createProducerLicense, uploadLicensePdf,
} from '@/services/licenses.service.js';

const AGENCY_LEGAL_DEFAULT = 'NDEMNIFLY Insurance Agency, LLC';
const AGENCY_DBA_DEFAULT = 'NDEMNIFLY Aviation Insurance';

const blankForm = {
  state: '', license_number: '', license_class: '',
  issue_date: '', expiration_date: '', renewal_fee_cents: '', state_portal_url: '',
  npn: '', is_resident: false,
};

// Create a license (+ optional PDF). Consolidates the spec's 4-step wizard
// into one sectioned form. kind fixed from the active tab. Producer broker
// defaults to the caller (locked for non-admin).
export default function UploadLicenseDialog({ open, onOpenChange, kind, me, onSaved }) {
  const isAgency = kind === 'agency';
  const [form, setForm] = useState(blankForm);
  const [drlp, setDrlp] = useState(null);   // agency only
  const [broker, setBroker] = useState(null); // producer only (admin picks)
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const canPickBroker = !!me?.is_admin;
  const effectiveBroker = canPickBroker ? broker : (me?.broker_contact_id ? { id: me.broker_contact_id, name: me.broker_name } : null);
  const stateOk = /^[A-Za-z]{2}$/.test(form.state.trim());
  const licOk = form.license_number.trim().length > 0;
  const brokerOk = isAgency || !!effectiveBroker?.id;
  const canSubmit = stateOk && licOk && brokerOk && !saving;

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  function reset() { setForm(blankForm); setDrlp(null); setBroker(null); setFile(null); }

  function buildBody() {
    const b = {
      state: form.state.trim().toUpperCase(),
      license_number: form.license_number.trim(),
      license_class: form.license_class.trim() || null,
      issue_date: form.issue_date || null,
      expiration_date: form.expiration_date || null,
      state_portal_url: form.state_portal_url.trim() || null,
    };
    const fee = form.renewal_fee_cents === '' ? null : Math.round(parseFloat(form.renewal_fee_cents) * 100);
    if (fee != null && !Number.isNaN(fee)) b.renewal_fee_cents = fee;
    if (isAgency) {
      b.agency_legal_name = AGENCY_LEGAL_DEFAULT;
      b.agency_dba = AGENCY_DBA_DEFAULT;
      if (drlp?.id) { b.drlp_broker_contact_id = drlp.id; b.drlp_name_snapshot = drlp.name; }
    } else {
      b.broker_contact_id = effectiveBroker.id;
      b.broker_name_snapshot = effectiveBroker.name || effectiveBroker.id;
      b.npn = form.npn.trim() || null;
      b.is_resident = !!form.is_resident;
    }
    return b;
  }

  async function save() {
    setSaving(true);
    try {
      const created = isAgency
        ? await createAgencyLicense(buildBody())
        : await createProducerLicense(buildBody());
      if (file) {
        try {
          await uploadLicensePdf(kind, created.id, file);
        } catch (upErr) {
          toast.error(`Saved, but PDF upload failed: ${upErr.message}. Retry from the detail page.`);
          finish(); return;
        }
      }
      const warn = created._warnings?.[0];
      toast.success(warn ? `License saved — ${warn}` : 'License saved');
      finish();
    } catch (err) {
      toast.error(`Save failed: ${err.body?.errors?.[0] || err.body?.error || err.code || err.message}`);
    } finally {
      setSaving(false);
    }
  }

  function finish() { onOpenChange(false); onSaved?.(); reset(); }

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add {isAgency ? 'agency' : 'producer'} license</DialogTitle>
          <DialogDescription>
            {isAgency ? 'Org-wide state license for NDEMNIFLY Insurance Agency, LLC.' : 'Per-state individual producer license.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          {/* PDF drop (optional) */}
          <label
            htmlFor="lic-file"
            className="flex min-h-[88px] cursor-pointer items-center justify-center rounded-card border-2 border-dashed border-border bg-bg/50 p-3 text-center transition-colors hover:border-accent/40"
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
            onDragOver={(e) => e.preventDefault()}
          >
            {file ? (
              <span className="flex items-center gap-2 text-sm text-text"><FileCheck2 size={18} className="text-success" /> {file.name}</span>
            ) : (
              <span className="flex flex-col items-center text-xs text-text-muted">
                <Upload size={20} strokeWidth={1.5} className="mb-1 text-text-dim" />
                Drop the license PDF here or <span className="text-accent">browse</span> <span className="text-text-dim">(optional — can add later)</span>
              </span>
            )}
            <input id="lic-file" type="file" accept=".pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <Field label="State (USPS)" required>
              <Input maxLength={2} placeholder="GA" value={form.state} onChange={set('state')} className="uppercase" />
            </Field>
            <Field label="License #" required>
              <Input value={form.license_number} onChange={set('license_number')} />
            </Field>
          </div>

          <Field label="License class">
            <Input placeholder={isAgency ? 'Non-Resident Business Entity Producer' : 'Resident Producer'} value={form.license_class} onChange={set('license_class')} />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Issue date"><Input type="date" value={form.issue_date} onChange={set('issue_date')} /></Field>
            <Field label="Expiration date"><Input type="date" value={form.expiration_date} onChange={set('expiration_date')} /></Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Renewal fee ($)"><Input type="number" min="0" step="0.01" placeholder="0.00" value={form.renewal_fee_cents} onChange={set('renewal_fee_cents')} /></Field>
            <Field label="State portal URL"><Input placeholder="https://…" value={form.state_portal_url} onChange={set('state_portal_url')} /></Field>
          </div>

          {isAgency ? (
            <Field label="DRLP (designated responsible licensed producer)">
              <CrmContactPicker value={drlp} onSelect={setDrlp} placeholder="Search DRLP…" />
            </Field>
          ) : (
            <>
              <Field label="Broker (licensee)">
                {canPickBroker
                  ? <CrmContactPicker value={broker} onSelect={setBroker} placeholder="Search broker…" />
                  : <div className="rounded-card border border-border bg-surface px-3 py-2 text-sm text-text-muted">{me?.broker_name || 'You'}</div>}
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="NPN"><Input placeholder="21054433" value={form.npn} onChange={set('npn')} /></Field>
                <Field label="Resident?">
                  <label className="flex h-11 items-center gap-2 text-sm text-text">
                    <input type="checkbox" checked={form.is_resident} onChange={(e) => setForm((p) => ({ ...p, is_resident: e.target.checked }))} />
                    Resident license
                  </label>
                </Field>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!canSubmit} onClick={save}>{saving ? 'Saving…' : 'Save & upload'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-text-muted">
        {label}{required && <span className="text-accent"> *</span>}
      </label>
      {children}
    </div>
  );
}
